import {
  Button,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import { classNames } from '@commenda/utils';
import { useState } from 'react';
import { MONTH_NAMES_SHORT } from '../../constants';
import { TaxPeriodMonthPickerProps } from '../../types';

interface ExtendedTaxPeriodMonthPickerProps extends TaxPeriodMonthPickerProps {
  isOptional?: boolean;
}

export function TaxPeriodMonthPicker({
  value,
  onChange,
  label,
  placeholder = 'Select month',
  minDate,
  maxDate,
  disabled = false,
  error,
  isOptional = false,
}: ExtendedTaxPeriodMonthPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(
    value?.year ?? new Date().getFullYear()
  );

  const handleMonthSelect = (month: number) => {
    onChange({ month, year: viewYear });
    setIsOpen(false);
  };

  const isMonthDisabled = (month: number): boolean => {
    if (minDate) {
      if (viewYear < minDate.year) return true;
      if (viewYear === minDate.year && month < minDate.month) return true;
    }
    if (maxDate) {
      if (viewYear > maxDate.year) return true;
      if (viewYear === maxDate.year && month > maxDate.month) return true;
    }
    return false;
  };

  const formatValue = (): string => {
    if (!value) return placeholder;
    return `${MONTH_NAMES_SHORT[value.month - 1]} ${value.year}`;
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="txt-small-medium text-text-secondary">
        {label}
        {!isOptional && (
          <span className="txt-small-medium text-utility-violet-600 ml-0.5">
            *
          </span>
        )}
        {isOptional && (
          <span className="txt-small-regular text-text-tertiary ml-1">
            (optional)
          </span>
        )}
      </label>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={classNames(
              'flex h-11 w-full items-center justify-between rounded-md border px-3 py-2.5',
              'bg-bg-primary txt-medium-regular transition-colors',
              'hover:border-border-primary-hover focus:outline-none focus:ring-2 focus:ring-ring',
              error ? 'border-utility-error-500' : 'border-border-primary',
              disabled && 'opacity-50 cursor-not-allowed',
              !value && 'text-text-placeholder'
            )}
            disabled={disabled}
          >
            <span>{formatValue()}</span>
            <Icons.ChevronDown className="size-4 text-text-tertiary" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          {/* Year Navigation */}
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setViewYear((y) => y - 1)}
              type="button"
            >
              <Icons.ChevronLeft className="size-4" />
            </Button>
            <span className="txt-small-bold text-text-primary">{viewYear}</span>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setViewYear((y) => y + 1)}
              type="button"
            >
              <Icons.ChevronRight className="size-4" />
            </Button>
          </div>

          {/* Month Grid */}
          <div className="grid grid-cols-3 gap-2">
            {MONTH_NAMES_SHORT.map((monthName, index) => {
              const monthNum = index + 1;
              const isSelected =
                value?.month === monthNum && value?.year === viewYear;
              const isDisabled = isMonthDisabled(monthNum);

              return (
                <Button
                  key={monthName}
                  variant={isSelected ? 'primary' : 'tertiary'}
                  size="sm"
                  className={classNames(
                    'w-full',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  disabled={isDisabled}
                  onClick={() => handleMonthSelect(monthNum)}
                  type="button"
                >
                  {monthName}
                </Button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="txt-xs-regular text-utility-error-500">{error}</p>
      )}
    </div>
  );
}
